import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, formatApiError, getStoredToken } from "@/lib/api";
import { EmptyState, PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DownloadSimple, Pause, PencilSimple, Play, Plus, SpeakerHigh, Trash } from "@phosphor-icons/react";

const voiceLabel = (voice) => voice ? `${voice.name} — ${voice.gender} · ${voice.accent}` : "";

const previewText = (text) => {
  const words = (text || "").trim().split(/\s+/).filter(Boolean);
  if (words.length <= 6) return words.join(" ");
  return `${words.slice(0, 6).join(" ")}…`;
};

export default function CustomerSpeech() {
  const [rows, setRows] = useState([]);
  const [paidVoices, setPaidVoices] = useState([]);
  const [freeVoices, setFreeVoices] = useState([]);
  const [paidModel, setPaidModel] = useState("");
  const [freeModel, setFreeModel] = useState("");
  const [loading, setLoading] = useState(true);
  const [setupError, setSetupError] = useState("");
  const [freeSetupError, setFreeSetupError] = useState("");
  const [mode, setMode] = useState(null); // "paid" | "free" | null
  const [editing, setEditing] = useState(null);
  const [text, setText] = useState("");
  const [voice, setVoice] = useState("");
  const [formError, setFormError] = useState("");
  const [listening, setListening] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioCtxRef = useRef(null);
  const abortRef = useRef(null);
  const clipAudioRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [clips, paidRes, freeRes] = await Promise.all([
        api.get("/speech/clips"),
        api.get("/speech/voices").catch((e) => ({ error: e })),
        api.get("/speech/free-voices").catch((e) => ({ error: e })),
      ]);
      setRows(clips.data || []);

      if (paidRes.error) {
        setPaidVoices([]);
        setPaidModel("");
        setSetupError(formatApiError(paidRes.error.response?.data?.detail));
      } else {
        const list = paidRes.data?.voices || [];
        setPaidVoices(list);
        setPaidModel(paidRes.data?.model || "");
        setSetupError("");
      }

      if (freeRes.error) {
        setFreeVoices([]);
        setFreeModel("");
        setFreeSetupError(formatApiError(freeRes.error.response?.data?.detail));
      } else {
        setFreeVoices(freeRes.data?.voices || []);
        setFreeModel(freeRes.data?.model || "AI Engine · Piper");
        setFreeSetupError("");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeVoices = mode === "free" ? freeVoices : paidVoices;

  const stopClip = () => {
    const audio = clipAudioRef.current;
    clipAudioRef.current = null;
    if (audio) {
      audio.pause();
      if (audio.src) URL.revokeObjectURL(audio.src);
    }
    setPlayingId(null);
  };

  const stopAudio = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx) ctx.close().catch(() => {});
    stopClip();
    setListening(false);
  };

  const closeDialog = () => {
    stopAudio();
    setMode(null);
    setEditing(null);
    setFormError("");
  };

  const openNew = (nextMode) => {
    stopAudio();
    const list = nextMode === "free" ? freeVoices : paidVoices;
    setEditing(null);
    setText("");
    setVoice(list[0]?.name || "");
    setFormError(nextMode === "free" ? freeSetupError : setupError);
    setMode(nextMode);
  };

  const openEdit = (row) => {
    stopAudio();
    const nextMode = row.engine === "free" ? "free" : "paid";
    const list = nextMode === "free" ? freeVoices : paidVoices;
    setEditing(row);
    setText(row.text || "");
    setVoice(row.voice || list[0]?.name || "");
    setFormError("");
    setMode(nextMode);
  };

  const playStream = async (response, ctx) => {
    const ctype = response.headers.get("content-type") || "";
    if (!ctype.includes("ndjson")) {
      const data = await response.json();
      const binary = atob(data.audio_base64 || "");
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: data.mime || "audio/wav" }));
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setListening(false); };
      await audio.play();
      return;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let pending = "";
    let nextAt = 0;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      pending += decoder.decode(value, { stream: true });
      let nl = pending.indexOf("\n");
      while (nl >= 0) {
        const line = pending.slice(0, nl).trim();
        pending = pending.slice(nl + 1);
        nl = pending.indexOf("\n");
        if (!line) continue;
        const msg = JSON.parse(line);
        if (msg.error) throw new Error(msg.error);
        if (msg.pcm && audioCtxRef.current === ctx) {
          const binary = atob(msg.pcm);
          const bytes = new Uint8Array(binary.length - (binary.length % 2));
          for (let i = 0; i < bytes.length; i += 1) bytes[i] = binary.charCodeAt(i);
          const samples = new Int16Array(bytes.buffer);
          if (!samples.length) continue;
          const buffer = ctx.createBuffer(1, samples.length, msg.rate || 24000);
          const channel = buffer.getChannelData(0);
          for (let i = 0; i < samples.length; i += 1) channel[i] = samples[i] / 32768;
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          const start = Math.max(ctx.currentTime + 0.05, nextAt);
          source.start(start);
          nextAt = start + buffer.duration;
        }
        if (msg.done) {
          const wait = Math.max(0, (nextAt - ctx.currentTime) * 1000);
          window.setTimeout(() => {
            if (audioCtxRef.current === ctx) setListening(false);
          }, wait + 80);
        }
      }
    }
  };

  const listenDemo = async () => {
    if (!text.trim() || !voice) return;
    stopAudio();
    setFormError("");
    setListening(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;
    ctx.resume().catch(() => {});
    try {
      const path = mode === "free" ? "/speech/free-demo" : "/speech/demo";
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getStoredToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text, voice }),
        signal: ctrl.signal,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(formatApiError(body.detail) || "Could not play the demo.");
      }
      await playStream(res, ctx);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setListening(false);
      const msg = e?.message || "Could not play the demo.";
      setFormError(msg);
      if (/insufficient|balance/i.test(msg)) toast.error(msg);
    }
  };

  const save = async () => {
    if (!text.trim() || !voice) return;
    stopAudio();
    setSaving(true);
    setFormError("");
    try {
      if (editing) {
        const { data } = await api.put(`/speech/clips/${editing.id}`, { text, voice });
        toast.success(
          data?.charged_amount_minor
            ? `Voice updated · charged $${(data.charged_amount_minor / 100).toFixed(2)}`
            : "Voice updated",
        );
      } else if (mode === "free") {
        await api.post("/speech/free-clips", { text, voice });
        toast.success("Free voice generated");
      } else {
        const { data } = await api.post("/speech/clips", { text, voice });
        toast.success(
          data?.charged_amount_minor
            ? `Voice generated · charged $${(data.charged_amount_minor / 100).toFixed(2)}`
            : "Voice generated",
        );
      }
      closeDialog();
      await load();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Could not generate this voice.";
      setFormError(msg);
      if (e.response?.status === 402) toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const playClip = async (row) => {
    if (playingId === row.id) {
      stopClip();
      return;
    }
    stopAudio();
    setPlayingId(row.id);
    try {
      const res = await fetch(`${API}/speech/clips/${row.id}/file`, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(formatApiError(body.detail) || "Could not play this voice.");
      }
      const url = URL.createObjectURL(await res.blob());
      const audio = new Audio(url);
      clipAudioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (clipAudioRef.current === audio) {
          clipAudioRef.current = null;
          setPlayingId(null);
        }
      };
      await audio.play();
    } catch (e) {
      stopClip();
      toast.error(e?.message || "Could not play this voice.");
    }
  };

  const download = async (row) => {
    setBusyId(row.id);
    try {
      const res = await fetch(`${API}/speech/clips/${row.id}/file`, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(formatApiError(body.detail) || "Could not download this voice.");
      }
      const blob = await res.blob();
      const named = (res.headers.get("content-disposition") || "").match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = named?.[1] || `${row.voice || "voice"}.wav`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.message || "Could not download this voice.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async () => {
    if (!confirm) return;
    setBusyId(confirm.id);
    try {
      await api.delete(`/speech/clips/${confirm.id}`);
      toast.success("Voice deleted");
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const dialogTitle = editing
    ? "Edit voice"
    : mode === "free"
      ? "Free Voices"
      : "Paid Voices";

  return (
    <div data-testid="customer-speech-page">
      <PageHeader title="Text to Speech" subtitle="Write a script, choose a voice, and save the audio to download later.">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => openNew("paid")} data-testid="speech-paid-open" className="rounded-lg">
            <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Paid Voices
          </Button>
          <Button
            variant="outline"
            onClick={() => openNew("free")}
            data-testid="speech-free-open"
            className="rounded-lg"
          >
            <SpeakerHigh className="w-4 h-4 mr-1.5" weight="bold" /> Free Voices
          </Button>
        </div>
      </PageHeader>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState
            testid="speech-empty"
            icon={SpeakerHigh}
            title="No voices yet"
            description="Generate a paid Gemini voice or a free AI-engine voice. Listen to the first 6 words before you save it."
          />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="speech-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Text</TableHead>
                  <TableHead>Voice</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead>Accent</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} data-testid={`speech-row-${row.id}`}>
                    <TableCell className="max-w-[16rem] whitespace-nowrap">
                      <span>{previewText(row.text)}</span>
                    </TableCell>
                    <TableCell>{row.voice}</TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        row.engine === "free"
                          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                          : "bg-primary/15 text-primary"
                      }`}>
                        {row.engine === "free" ? "Free" : "Paid"}
                      </span>
                    </TableCell>
                    <TableCell>{row.gender}</TableCell>
                    <TableCell>{row.accent}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {row.created_at ? new Date(row.created_at).toLocaleString() : ""}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0" disabled={busyId === row.id}
                          onClick={() => playClip(row)} data-testid={`speech-play-${row.id}`}
                          title={playingId === row.id ? "Stop" : "Play"}
                          aria-label={playingId === row.id ? "Stop" : "Play"}>
                          {playingId === row.id
                            ? <Pause className="w-4 h-4" weight="fill" />
                            : <Play className="w-4 h-4" weight="fill" />}
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0" disabled={busyId === row.id}
                          onClick={() => download(row)} data-testid={`speech-download-${row.id}`}
                          title="Download" aria-label="Download">
                          <DownloadSimple className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0" disabled={busyId === row.id}
                          onClick={() => openEdit(row)} data-testid={`speech-edit-${row.id}`} title="Edit" aria-label="Edit">
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0 text-destructive" disabled={busyId === row.id}
                          onClick={() => setConfirm(row)} data-testid={`speech-delete-${row.id}`} title="Delete" aria-label="Delete">
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      <Dialog open={!!mode} onOpenChange={(v) => { if (!v) closeDialog(); }}>
        <DialogContent data-testid="speech-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>
              {mode === "free"
                ? "Free voice to listen demo plays the first 6 words."
                : "Write the script, then choose a paid voice. Listen demo plays the first 6 words."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Text</Label>
              <Textarea data-testid="speech-text" value={text} maxLength={4000}
                onChange={(e) => setText(e.target.value)} className="min-h-[120px]"
                placeholder="Write the words you want spoken" />
            </div>
            <div className="space-y-2">
              <Label>Voice</Label>
              <Select value={voice || undefined} onValueChange={setVoice} disabled={!activeVoices.length}>
                <SelectTrigger data-testid="speech-voice">
                  <SelectValue placeholder={activeVoices.length ? "Select a voice" : "No voices"} />
                </SelectTrigger>
                <SelectContent>
                  {activeVoices.map((item) => (
                    <SelectItem key={item.name} value={item.name}>{voiceLabel(item)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formError && <p className="text-sm text-destructive" data-testid="speech-error">{formError}</p>}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={listenDemo} disabled={listening || saving || !text.trim() || !voice}
              data-testid="speech-listen" className="rounded-lg">
              {listening ? "Listening…" : "Listen demo"}
            </Button>
            <Button onClick={save} disabled={saving || listening || !text.trim() || !voice}
              data-testid="speech-save" className="rounded-lg">
              {saving ? "Generating…" : mode === "free" ? "Generate Free Voice" : "Generate Voice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this voice?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.voice} will be removed. The audio file cannot be downloaded after this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="speech-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
